import { useLazyQuery, useMutation } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import SAVE_PROGRAMA_CEAO_NUTRICIONISTA, { adaptAddVisitaCEAONutricionistaInput } from "../../../../../../data/graphQL/mutations/Visitas/saveProgramaCEAONutricionista";
import SAVE_PROGRAMA_CEAE_NUTRICIONISTA, { adaptAddVisitaCEAENutricionistaInput } from "../../../../../../data/graphQL/mutations/Visitas/saveProgramaCEAENutricionista";
import { useStepSave } from "../../../../hooks/useStepSave";
import CardBox from "../../CardBox/CardBox";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { IndicacionesVisitaInput } from "./components/IndicacionesVisitaInput";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { ID_TIPO_PROFESIONAL_ENFERMERO } from "../../../../hooks/useLoadVisita";
import { GET_SOLICITUD_INDICACION_PENDIENTE } from "../../../../../../data/graphQL/queries/GestionAsistencial/getSolicitudIndicacionPendiente";

const deshabilitadosParaNutri = ['listaIndicacion', 'justificacionModificacion', 'indicacionComentario'];

export default function ProgramaCEAOCEAENutricionista({ readOnly, onSaveRef, onExposeFormatted, complicacionesCombo, programa, esUrgencia }) {
  const [formData, setFormData] = useState({});
  const { visita, setVisita } = useVisita();
  const [solicitudIndicacionPendiente, setSolicitudIndicacionPendiente] = useState(null);

  const [save] = useMutation(programa === 'CEAO' ? SAVE_PROGRAMA_CEAO_NUTRICIONISTA : SAVE_PROGRAMA_CEAE_NUTRICIONISTA);

  const formattedGroups = useMemo(() => {
    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };

        if (programa === 'CEAO' && visita?.idTipoProfeisonal === ID_TIPO_PROFESIONAL_ENFERMERO && deshabilitadosParaNutri.includes(newInput.name)) {
          newInput.disabled = true;
        }

        if (input.name === "complicaciones") {
          const ningunaId = complicacionesCombo?.find((c) => c.displayName.toLowerCase() === 'ninguna')?.id;
          newInput.options = complicacionesCombo?.map((opt) => {
            if (Array.isArray(formData.complicaciones)) {
              const hasNinguna = formData.complicaciones.includes(ningunaId);
              const hasOther = formData.complicaciones.some((id) => id !== ningunaId);
              if (hasNinguna) {
                // Si "ninguna" está seleccionada, deshabilitar todas menos "ninguna"
                return { ...opt, disabled: opt.id !== ningunaId };
              }
              if (hasOther) {
                // Si hay alguna distinta de "ninguna", deshabilitar "ninguna"
                return { ...opt, disabled: opt.id === ningunaId };
              }
            }
            return { ...opt, disabled: false };
          });
        }

        if (readOnly) newInput.disabled = true;
        if (esUrgencia) newInput.required = false;

        return newInput;
      });

      const hidden = !formattedInputs.some((i) => !i.hidden);
      return { ...group, hidden, inputs: formattedInputs };
    });
  }, [readOnly, complicacionesCombo, formData?.complicaciones, esUrgencia]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const indicaciones = Array.isArray(formData.listaIndicacion) ? formData.listaIndicacion : [];
    let totalCalorias = 0;

    indicaciones.forEach((indicacion) => {
      const cantidad = Number(indicacion.cantidadDiaria) || 0;
      const calorias = Number(indicacion.calorias) || 0;
      totalCalorias += cantidad * calorias;
    });

    setFormData((prev) => ({ ...prev, caloriasSO: totalCalorias }));
  }, [formData.listaIndicacion]);

  // %SO = (caloriasCubiertasSOTotales / requerimientoCaloricoTotal) * 100
  useEffect(() => {
    setFormData((prev) => {
      const req = Number(prev.requerimientoCaloricoTotal);
      const cub = Number(prev.caloriasCubiertasSOTotales);
      const pct = req > 0 && !Number.isNaN(cub) ? Math.round(cub / req * 100) : null;
      if (pct === prev.porcentajeSO) return prev;
      return { ...prev, porcentajeSO: pct };
    });
  }, [formData.requerimientoCaloricoTotal, formData.caloriasCubiertasSOTotales]);

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedGroups, formData });

    if (programa === 'CEAO') {
      const { data } = await save({ variables: { input: adaptAddVisitaCEAONutricionistaInput(formData) } });

      const idVisitaCeao = data?.saveVisitaCeaoNutricionista?.idVisitaCeao;
      if (!idVisitaCeao) throw new Error("Error al guardar");
      setVisita((prev) => ({ ...prev, ...formData, idVisitaCeao }));
    } else {
      const { data } = await save({ variables: { input: adaptAddVisitaCEAENutricionistaInput(formData) } });

      const idVisitaCeae = data?.saveVisitaCeaeNutricionista?.idVisitaCeae;
      if (!idVisitaCeae) throw new Error("Error al guardar");
      setVisita((prev) => ({ ...prev, ...formData, idVisitaCeae }));
    }
  });

  const [getSolicitudIndicacionPendiente, { data: dataSolicitudPendiente }] = useLazyQuery(GET_SOLICITUD_INDICACION_PENDIENTE, {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!dataSolicitudPendiente) return;
    setSolicitudIndicacionPendiente(dataSolicitudPendiente.solicitudIndicacionPendiente.success && dataSolicitudPendiente.solicitudIndicacionPendiente.valorEsperado != null);
  }, [dataSolicitudPendiente]);

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(groups, true);

      if (programa === 'CEAO') {
        setFormData({
          ...filterVisitaData(visita, inputNames),
          idVisitaCeao: visita.idVisitaCeao,
          idVisita: visita.idVisita
        });
      } else {
        setFormData({
          ...filterVisitaData(visita, inputNames),
          idVisitaCeae: visita.idVisitaCeae,
          idVisita: visita.idVisita
        });
      }

    }
  }, [visita]);

  
  useEffect(() => {
    if (!visita?.idEvaluacion) return;
    
    getSolicitudIndicacionPendiente({ variables: { idEvaluacion: visita?.idEvaluacion } });
  }, [visita?.idEvaluacion]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "programaCEAOCEAENutricionista",
      stepName: "Programa CEAO - CEA/E Nutricionista",
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
      {formattedGroups.
        filter((group) => !group.hidden).
        map((group, idx) =>
          <CardBox key={idx} title={group.title}>
            <Grid container spacing={2}>
              {group.inputs.
                filter((input) => !input.hidden).
                map((input) =>
                  <Grid size={{ xs: 12 }} key={input.name}>
                    {input.name !== "listaIndicacion" ?
                      <RenderInputByTypeMobile
                        input={input}
                        value={formData[input.name]}
                        handleChange={handleChange}
                        row={input.type === "multiple_boolean"} /> :


                      <IndicacionesVisitaInput
                        readOnly={true}
                        handleChange={handleChange}
                        indicaciones={formData[input.name]}
                        inputName={input.name}
                        idEvaluacion={visita?.idEvaluacion}
                        solicitudIndicacionPendiente={solicitudIndicacionPendiente}
                      />
                    }
                  </Grid>
                )}
            </Grid>
          </CardBox>
        )}
    </>);

}

const groups = [
  {
    title: "Indicaciones",
    inputs: [
      {
        name: "listaIndicacion",
        displayName: "Indicaciones (productos)",
        type: "custom",
        required: true
      },
      {
        name: "justificacionModificacion",
        displayName: "Justificación por modificación",
        type: "long_string",
        maxLength: 1000
      },
      {
        name: "indicacionComentario",
        displayName: "Comentarios de indicación",
        type: "long_string",
        maxLength: 4000
      }]

  },
  {
    title: "Requerimientos",
    inputs: [
      {
        name: "caloriasTotales",
        displayName: "Requerimiento calórico total",
        type: "float"
      },
      {
        name: "caloriasSO",
        displayName: "Calorías cubiertas por SO (totales)",
        type: "float",
        required: true
      },
      {
        name: "porcentajeSO",
        displayName: "%SO",
        type: "int"
      },
      {
        name: "complicaciones",
        displayName: "Complicaciones",
        type: "multiple_select",
        required: true
      }]

  }];